import type * as React from "react";
import { Icon } from "../icon/Icon";

/**
 * Low-emphasis inline text action, optionally with a trailing chevron.
 * Ported from the Wanted Design System bundle (components/actions/TextButton.jsx).
 */
export interface TextButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "color"> {
  /** @default "primary" */
  color?: "primary" | "assistive" | "normal";
  /** @default "medium" */
  size?: "medium" | "small";
  /** Show a trailing chevron-right. */
  chevron?: boolean;
  underline?: boolean;
  disabled?: boolean;
  children?: React.ReactNode;
}

const SIZES = {
  medium: { font: "var(--font-size-body-2)", lh: "var(--line-height-body-2)", icon: 16 },
  small: { font: "var(--font-size-label-1)", lh: "var(--line-height-label-1)", icon: 14 },
} as const;

export function TextButton({
  children,
  color = "primary",
  size = "medium",
  disabled = false,
  chevron = false,
  underline = false,
  style,
  ...rest
}: TextButtonProps) {
  const s = SIZES[size] ?? SIZES.medium;
  const c = disabled
    ? "var(--label-disable)"
    : color === "primary"
      ? "var(--primary-normal)"
      : color === "assistive"
        ? "var(--label-neutral)"
        : "var(--label-normal)";
  return (
    <button
      type="button"
      disabled={disabled}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 2,
        padding: 0,
        border: "none",
        background: "transparent",
        color: c,
        fontFamily: "var(--font-sans)",
        fontSize: s.font,
        lineHeight: s.lh,
        fontWeight: "var(--font-weight-semibold)",
        textDecoration: underline ? "underline" : "none",
        textUnderlineOffset: 2,
        cursor: disabled ? "not-allowed" : "pointer",
        WebkitTapHighlightColor: "transparent",
        ...style,
      }}
      {...rest}
    >
      {children}
      {chevron && <Icon name="chevronRight" size={s.icon} />}
    </button>
  );
}

export default TextButton;
